import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Radio, Search, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["open", "pending", "in_progress", "done", "cancelled"];
const STATUS_LABELS = { open: "Open", pending: "Pending", in_progress: "In Progress", done: "Done", cancelled: "Cancelled" };
const STATUS_CLS = {
  open: "bg-slate-100 text-slate-600",
  pending: "bg-amber-50 text-amber-700",
  in_progress: "bg-blue-50 text-blue-700",
  done: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-700",
};

export default function SalesTasksPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tasks, setTasks] = useState([]);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [assignedTo, setAssignedTo] = useState("ALL");
  const [region, setRegion] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // The Assigned-To filter only makes sense for admins — a Sales / CRM
  // user's task list is always server-side scoped to their own tasks
  // regardless of this param (see backend/tasks.py's list_tasks), and
  // GET /api/users is itself admin-only.
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try { const { data } = await api.get("/users"); setUsers(data || []); } catch { /* ignore */ }
    })();
  }, [isAdmin]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tasks", {
        params: {
          q: q || undefined,
          source: source === "ALL" ? undefined : source,
          status: status === "ALL" ? undefined : status,
          assigned_to: isAdmin && assignedTo !== "ALL" ? assignedTo : undefined,
          region: region || undefined,
        },
      });
      setTasks(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load tasks"); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [source, status, assignedTo]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, region]);

  const updateStatus = async (task, newStatus) => {
    try {
      const { data } = await api.patch(`/tasks/${task.id}`, { status: newStatus });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? data : t)));
      toast.success(`${task.title} → ${STATUS_LABELS[newStatus]}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="sales-tasks-page">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Sales Report</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
          Tasks
        </h1>
        <p className="text-sm text-slate-500 mt-1">Follow-ups raised manually or auto-created from IndiaMART inquiries.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-end gap-3" data-testid="tasks-filters">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs text-slate-500 font-normal">Search</Label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input className="pl-8" placeholder="Task title…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tasks-search-input" />
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-500 font-normal">Source</Label>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="w-44 mt-1" data-testid="tasks-source-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All sources</SelectItem>
              <SelectItem value="IndiaMART">IndiaMART</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500 font-normal">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40 mt-1" data-testid="tasks-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {isAdmin && (
          <div>
            <Label className="text-xs text-slate-500 font-normal">Assigned To</Label>
            <Select value={assignedTo} onValueChange={setAssignedTo}>
              <SelectTrigger className="w-48 mt-1" data-testid="tasks-assigned-to-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Everyone</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.email}>{u.name || u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-xs text-slate-500 font-normal">Region</Label>
          <Input className="w-36 mt-1" placeholder="e.g. Karnataka" value={region} onChange={(e) => setRegion(e.target.value)} data-testid="tasks-region-filter" />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
        {!loading && tasks.length === 0 && (
          <div className="text-center text-slate-500 text-sm py-10" data-testid="no-tasks">No tasks yet.</div>
        )}
        {tasks.map((t) => (
          <div key={t.id} className="p-4 flex items-start justify-between gap-4" data-testid={`task-row-${t.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-slate-800">{t.title}</span>
                {t.source === "IndiaMART" && (
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 flex items-center gap-1" data-testid={`task-indiamart-badge-${t.id}`}>
                    <Radio className="w-3 h-3" /> IndiaMART
                  </span>
                )}
                {t.needs_manual_assignment && (
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 flex items-center gap-1" data-testid={`task-needs-manual-badge-${t.id}`}>
                    <AlertTriangle className="w-3 h-3" /> Needs manual assignment
                  </span>
                )}
              </div>
              {t.description && <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap line-clamp-3">{t.description}</div>}
              <div className="text-[11px] text-slate-500 mt-1.5 flex items-center gap-3 flex-wrap">
                {t.assigned_to && <span>Assigned: {t.assigned_to}</span>}
                {t.region && <span>Region: {t.region}</span>}
                {t.due_date && <span>Due: {t.due_date}</span>}
              </div>
              {t.assignment_reason && <div className="text-[11px] text-slate-400 mt-0.5" data-testid={`task-assignment-reason-${t.id}`}>{t.assignment_reason}</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_CLS[t.status]}`}>{STATUS_LABELS[t.status]}</span>
              <Select value={t.status} onValueChange={(v) => updateStatus(t, v)}>
                <SelectTrigger className="w-36 h-8" data-testid={`task-status-select-${t.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                </SelectContent>
              </Select>
              {t.status === "done" && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
